import * as THREE from "three";
import {
  Color,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  ShapeGeometry,
  Vector2,
  Vector3,
} from "three";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import { Line2 } from "three/examples/jsm/lines/Line2";
import SpriteText from "three-spritetext";
import { debug as d } from "debug";
import { HeadNode } from "./nodes-and-links-models";
import { headNodeParameters, leaderTypesForHead } from "./3d-rendering-parameters";

const debug = d("cs.heatmaps");

export const graphOptions: any = {
  size: { width: window.innerWidth, height: window.innerHeight },
};

const { headSize, scale, circleSize, hoverColourScalar } = headNodeParameters;

function createCrosshairs(): Group {
  const crosshairMaterial = new LineMaterial({
    color: 0x736f6e,
    linewidth: 2,
    opacity: 0.12,
    vertexColors: false,
    transparent: true,
  });
  crosshairMaterial.resolution.set(graphOptions.size.width, graphOptions.size.height);

  const verticalGeometry = new LineGeometry();
  verticalGeometry.setPositions([-0.93 * headSize, 0, -1, 0.93 * headSize, 0, -1]);
  const verticalLine = new Line2(verticalGeometry, crosshairMaterial);

  const horizontalGeometry = new LineGeometry();
  horizontalGeometry.setPositions([0, -0.93 * headSize, -1, -0.2, 0.93 * headSize, -1]);
  const horizontalLine = new Line2(horizontalGeometry, crosshairMaterial);

  const group = new Group();
  group.add(verticalLine);
  group.add(horizontalLine);
  return group;
}

const svgLoader = new SVGLoader();
const headImagePromise: Promise<Object3D> = new Promise((resolve, reject) => {
  svgLoader.load(
    "/Content/head_study/head-solo.svg",
    (data) => {
      const path = data.paths[0];

      const points2d: Vector2[] = path.currentPath.getPoints();
      const points: number[] = [];
      points2d.forEach((point) => {
        points.push(point.x, point.y, 0);
      });

      const geometry = new LineGeometry();
      geometry.setPositions(points);
      const lineMat = new LineMaterial({ color: 0x808080, linewidth: 8, vertexColors: false });
      lineMat.resolution.set(graphOptions.size.width, graphOptions.size.height);

      const lines = new Line2(geometry, lineMat);

      const group = new Group();
      group.add(lines);

      const fillMaterial = new MeshBasicMaterial({
        color: 0xffffff,
        opacity: 0.8,
        transparent: true,
        side: DoubleSide,
      });

      const fillShapes = SVGLoader.createShapes(path);
      fillShapes.forEach((shape) => {
        const shapeGeometry = new ShapeGeometry(shape);
        const shapeMesh = new Mesh(shapeGeometry, fillMaterial); // glassyMaterial);
        group.add(shapeMesh);
      });

      group.rotateX(-Math.PI);
      group.scale.multiply(new THREE.Vector3(-1, 1, -1));
      group.translateX(3.35 * scale);
      group.translateY(-3.2 * scale);
      group.scale.multiplyScalar(0.3);

      resolve(group);
    },
    () => {
      // Nothing for progress, should be quick
    },
    (errorEvent) => {
      reject(errorEvent);
    },
  );
});

function makeSphere(radius: number, position: Vector3, colourHex: string): Object3D {
  const geometry = new THREE.SphereGeometry(radius, 20, 20);
  // const sphereMaterial = new THREE.MeshBasicMaterial({
  //   color: new Color(colourHex),
  // });
  const sphereMaterial = new THREE.MeshStandardMaterial({
    color: new Color(colourHex),
    metalness: 0.2,
    roughness: 0.5,
    fog: true,
  });
  const mesh = new THREE.Mesh(geometry, sphereMaterial);

  mesh.position.add(position);
  return mesh;
}

function makeArc(
  radius: number,
  thickness: number,
  position: Vector3,
  colourHex: string,
  callbacks: { onHover: (() => void) | null; onHoverEnd: (() => void) | null } | null = null,
): Object3D {
  const circleGeometry = new THREE.CircleGeometry(radius, 32, 0, Math.PI * 2); // THREE.BoxGeometry(radius, radius, radius); // THREE.CircleGeometry(radius, 32);
  const material = new THREE.MeshBasicMaterial({
    color: colourHex,
    side: DoubleSide,
    opacity: 0,
    transparent: true,
  });
  const circle = new THREE.Mesh(circleGeometry, material);
  const curve = new THREE.EllipseCurve(0, 0, radius, radius, 0, 2 * Math.PI, false, 0);
  const points2d = curve.getPoints(50);
  const points: number[] = [];
  points2d.forEach((point) => {
    points.push(point.x, point.y, 0);
  });

  const geometry = new LineGeometry();
  geometry.setPositions(points);

  const standardColour = new Color(colourHex);
  const darkenedColour = new Color(colourHex).multiplyScalar(hoverColourScalar);

  const lineMat = new LineMaterial({
    color: standardColour.getHex(),
    linewidth: thickness,
    vertexColors: false,
  });
  lineMat.resolution.set(graphOptions.size.width, graphOptions.size.height); // window.innerWidth, window.innerHeight);
  const ellipse = new Line2(geometry, lineMat);

  if (callbacks) {
    callbacks.onHover = () => {
      lineMat.color = darkenedColour;
      lineMat.needsUpdate = true;
    };

    callbacks.onHoverEnd = () => {
      lineMat.color = standardColour;
      lineMat.needsUpdate = true;
    };
  }

  const group = new Group();
  group.add(ellipse);
  group.add(circle);
  group.position.add(position);
  return group;
}

function makeCircle(
  radius: number,
  position: Vector3,
  colourHex: string,
  opacity = 1,
  callbacks: { onHover: (() => void) | null; onHoverEnd: (() => void) | null } | null = null,
): Object3D {
  const circleGeometry = new THREE.CircleGeometry(radius, 32, 0, Math.PI * 2); // THREE.BoxGeometry(radius, radius, radius); // THREE.CircleGeometry(radius, 32);
  const standardColour = new Color(colourHex);
  const darkenedColour = new Color(colourHex).multiplyScalar(hoverColourScalar);

  const material = new THREE.MeshBasicMaterial({
    color: standardColour.getHex(),
    side: DoubleSide,
    opacity,
    transparent: opacity < 1,
  });
  if (callbacks) {
    callbacks.onHover = () => {
      material.color = darkenedColour;
      material.needsUpdate = true;
    };

    callbacks.onHoverEnd = () => {
      material.color = standardColour;
      material.needsUpdate = true;
    };
  }

  const circle = new THREE.Mesh(circleGeometry, material);
  circle.position.add(position);
  return circle;
}

function makeText(text: string, position: Vector3, textHeight: number) {
  const sprite = new SpriteText(text);
  sprite.material.depthTest = false;
  sprite.color = "black";
  sprite.textHeight = textHeight;
  sprite.position.add(position);
  return sprite;
}

export default function createHead3d(node: HeadNode): Object3D {
  debug("createHead3d", { width: graphOptions.size.width, height: graphOptions.size.height });
  const group = new THREE.Group();
  if (node.type === "Team" || node.type === "Aggregate") {
    headImagePromise.then((model) => {
      const headImage = model.clone();
      group.add(headImage);
    });
  } else {
    const bigCircle = makeCircle(headSize, new Vector3(0, 0, 0), "#ffff00", 0.01);
    group.add(bigCircle);
  }

  group.add(createCrosshairs());

  leaderTypesForHead.forEach((type) => {
    const fit = Math.max(3, node.data?.soloFits[type.leaderType] || 0);
    const radius = circleSize * (fit / 100);
    const position = new Vector3(-type.x * scale, type.y * scale, -1);
    const callbacks = {
      onHover: null,
      onHoverEnd: null,
    };
    const circle =
      node.type === "Team" || node.type === "Aggregate"
        ? makeArc(radius, 4, position, type.colour, callbacks)
        : makeCircle(radius, position, type.colour, 1, callbacks);
    circle.name = `leader-circle-${type.leaderType}`;
    circle.userData.leaderType = type.leaderType;
    circle.userData.value = node.data?.soloFits[type.leaderType];
    circle.userData.callbacks = callbacks;
    group.add(circle);
  });

  if (node.type === "Individual") {
    group.add(makeText(node.name, new Vector3(0, -1.6 * headSize, -1), 20));
  }

  // Rotate by 180 degrees to face the right way...
  group.rotateY(Math.PI);

  // Wrap in a separate group to make rotations of the entire head easy
  const outerGroup = new Group();
  outerGroup.add(group);
  outerGroup.name = `node-${node.id}`;

  return outerGroup;
}
